import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { VendorOpaqueIdString } from "../../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { MessagingChannelAddress } from "../../commonSubSchemas/messagingChannelAddress.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    VersionEventActorFieldsWithDefaults,
    VersionEventActorSchemaTypes,
} from "../../types/versionEventActor.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "VersionEventActor" as const;

/**
 * Kinds of actor that can trigger a version transition. Spelled `actorType` rather than `type`,
 * which mongoose reads as a path-type declaration.
 *
 * TODO: Add "system" — `{ actorType: "system"; subsystem: string }` — so internal writes (periodic
 * jobs, reconcilers, warning creation) name the subsystem responsible instead of leaving `changedBy`
 * absent. That means supplying a subsystem at every internal SCD write site, so it is its own piece
 * of work. "agent" and "technician" arms are likewise expected eventually.
 */
export const VERSION_EVENT_ACTOR_TYPES = ["user", "messagingChannel", "accessKey"] as const;

export type VersionEventActorType = (typeof VERSION_EVENT_ACTOR_TYPES)[number];

const IDENTITY_FIELDS = ["descopeUserId", "address", "accessKeyId", "accessKeyName"] as const;

type IdentityField = (typeof IDENTITY_FIELDS)[number];

/** The identity fields each arm owns; at least one must be present. A new arm means a new entry here. */
const IDENTITY_FIELDS_BY_ACTOR_TYPE = {
    user: ["descopeUserId"],
    messagingChannel: ["address"],
    accessKey: ["accessKeyId", "accessKeyName"],
} as const satisfies Record<VersionEventActorType, readonly IdentityField[]>;

type ActorIdentities = { actorType?: VersionEventActorType } & Partial<Record<IdentityField, unknown>>;

function ownedIdentityFields(actorType: VersionEventActorType | undefined): readonly IdentityField[] {
    return actorType === undefined ? [] : IDENTITY_FIELDS_BY_ACTOR_TYPE[actorType];
}

function isSet(value: unknown): boolean {
    return value !== undefined && value !== null;
}

/** Matches Mongoose's String `required`, which treats `""` as missing. */
function isPresent(value: unknown): boolean {
    return isSet(value) && value !== "";
}

/**
 * At least one of the arm's identity fields must be present and every other arm's absent, so an audit
 * record can never name a `user` by phone number or carry no identity at all. Shared by the Mongoose
 * paths and the DTO refinement below, so the two layers cannot disagree about what a well-formed actor is.
 */
function actorPairingIssues(actor: ActorIdentities): { field: IdentityField; message: string }[] {
    // No arm to check against — a partial update DTO may legitimately omit it.
    if (actor.actorType === undefined) return [];

    const owned = ownedIdentityFields(actor.actorType);
    const missing = owned.some((field) => isPresent(actor[field]))
        ? []
        : [{ field: owned[0], message: `${owned.join(" or ")} is required for a "${actor.actorType}" actor` }];
    const foreign = IDENTITY_FIELDS.filter((field) => !owned.includes(field) && isSet(actor[field])).map((field) => ({
        field,
        message: `${field} does not belong to a "${actor.actorType}" actor`,
    }));
    return [...missing, ...foreign];
}

/** `this` is the subdocument under validation, so these have to be `function`s rather than arrows. */
function isIdentityFieldRequired(this: VersionEventActor, field: IdentityField): boolean {
    const owned = ownedIdentityFields(this.actorType);
    return owned.includes(field) && (this[field] === "" || !owned.some((ownedField) => isPresent(this[ownedField])));
}

function identityFieldBelongsToArm(this: VersionEventActor, field: IdentityField, value: unknown): boolean {
    return !isSet(value) || ownedIdentityFields(this.actorType).includes(field);
}

/**
 * Who triggered a version transition. Every stored actor carries `actorType`, so later arms are
 * additive — no backfill of existing events.
 *
 * A discriminated union on `actorType`, spelled as optional fields because the decorators compose
 * per-field validators only:
 *
 * - `{ actorType: "user"; descopeUserId: string }`
 * - `{ actorType: "messagingChannel"; address: MessagingChannelAddress }`
 * - `{ actorType: "accessKey"; accessKeyId?: string; accessKeyName?: string }`, at least one of the two
 *
 * The pairing is enforced at both layers — conditionally-required Mongoose paths and a `superRefine`
 * over the generated DTO validators — so neither a missing nor a foreign identity can be stored. Read
 * an identity field only after narrowing on `actorType`.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<VersionEventActor>>(...addSchemaKeyHook(SCHEMA_KEY))
export class VersionEventActor extends SchemaBase<VersionEventActor, VersionEventActorFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.enum(VERSION_EVENT_ACTOR_TYPES) })
    @prop({ type: () => String, required: true, enum: VERSION_EVENT_ACTOR_TYPES })
    public actorType!: VersionEventActorType;

    /** Descope user id, an opaque string such as `U2abc...`. The `user` arm's identity. */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({
        type: () => String,
        required: function (this: VersionEventActor) {
            return isIdentityFieldRequired.call(this, "descopeUserId");
        },
        validate: {
            validator: function (this: VersionEventActor, value: unknown) {
                return identityFieldBelongsToArm.call(this, "descopeUserId", value);
            },
            message: 'descopeUserId belongs to a "user" actor',
        },
    })
    public descopeUserId?: string;

    /** The endpoint that sent the triggering message, channel included. The `messagingChannel` arm's identity. */
    @validate({ childClass: MessagingChannelAddress, childClassTransform: (object) => object.optional() })
    @prop({
        type: () => MessagingChannelAddress,
        _id: false,
        required: function (this: VersionEventActor) {
            return isIdentityFieldRequired.call(this, "address");
        },
        validate: {
            validator: function (this: VersionEventActor, value: unknown) {
                return identityFieldBelongsToArm.call(this, "address", value);
            },
            message: 'address belongs to a "messagingChannel" actor',
        },
    })
    public address?: MessagingChannelAddress;

    /** Descope access key id. One of the `accessKey` arm's identities. */
    @validate({ zodValidator: VendorOpaqueIdString.optional() })
    @prop({
        type: () => String,
        required: function (this: VersionEventActor) {
            return isIdentityFieldRequired.call(this, "accessKeyId");
        },
        validate: {
            validator: function (this: VersionEventActor, value: unknown) {
                return identityFieldBelongsToArm.call(this, "accessKeyId", value);
            },
            message: 'accessKeyId belongs to an "accessKey" actor',
        },
    })
    public accessKeyId?: string;

    /**
     * Name of the Descope access key, from its `accessKeyName` custom claim. One of the `accessKey` arm's
     * identities.
     */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({
        type: () => String,
        required: function (this: VersionEventActor) {
            return isIdentityFieldRequired.call(this, "accessKeyName");
        },
        validate: {
            validator: function (this: VersionEventActor, value: unknown) {
                return identityFieldBelongsToArm.call(this, "accessKeyName", value);
            },
            message: 'accessKeyName belongs to an "accessKey" actor',
        },
    })
    public accessKeyName?: string;

    static getters = {} as const satisfies Record<string, (self: VersionEventActor) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<VersionEventActorSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<VersionEventActor, VersionEventActorFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

/**
 * The decorator system has no object-level hook, so the arm↔identity rule is layered on after generation
 * — over every variant, since a malformed actor must not pass any of them.
 */
const generatedValidators = getZodSchemaFromClass(VersionEventActor) as unknown as Record<string, z.ZodType>;

VersionEventActor.dtoValidators = Object.fromEntries(
    Object.entries(generatedValidators).map(([variant, validator]) => [
        variant,
        validator.superRefine((actor, ctx) => {
            actorPairingIssues(actor as ActorIdentities).forEach(({ field, message }) => {
                ctx.addIssue({ code: "custom", path: [field], message });
            });
        }),
    ]),
) as unknown as DtoValidatorTypes<VersionEventActorSchemaTypes>;
